import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, EyeOff, Loader2, ArrowLeft, Mail, Shield, Smartphone } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { CheckEmailModal } from './CheckEmailModal';
import { requestPasswordReset, enable2FA, verify2FASetup } from '../../services/api';
import { API_URL } from '../../lib/env';
import { AuthLegalFooter } from './AuthLegalFooter';

// Helper to format API error messages for users
function formatErrorMessage(error: string): string {
  // Handle common API error messages
  if (error.toLowerCase().includes('already registered') || error.toLowerCase().includes('already exists')) {
    return 'This email is already registered. Please sign in instead.';
  }
  if (error.toLowerCase().includes('invalid credentials') || error.toLowerCase().includes('invalid email or password')) {
    return 'Invalid email or password. Please try again.';
  }
  if (error.toLowerCase().includes('password') && error.toLowerCase().includes('6')) {
    return 'Password must be at least 6 characters long.';
  }
  return error;
}

export function AuthModal() {
  const {
    showAuthModal,
    authModalMode,
    isLoading,
    closeAuthModal,
    setAuthModalMode,
    login,
    signup,
    requires2FA,
    verify2FALogin,
    reset2FAState,
  } = useAuthStore();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showCheckEmail, setShowCheckEmail] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Signup opt-in for 2FA
  const [wantEnable2FA, setWantEnable2FA] = useState(false);

  // 2FA login verification state
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const twoFAInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Post-signup 2FA setup state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [setupQRUrl, setSetupQRUrl] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupBackupCodes, setSetupBackupCodes] = useState<string[]>([]);
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [setupSuccess, setSetupSuccess] = useState(false);

  const handle2FACodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...twoFACode];
    newCode[index] = value;
    setTwoFACode(newCode);
    if (value && index < 5) {
      twoFAInputRefs.current[index + 1]?.focus();
    }
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handle2FASubmit(newCode.join(''));
    }
  };

  const handle2FAKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      twoFAInputRefs.current[index - 1]?.focus();
    }
  };

  const handle2FASubmit = async (code?: string) => {
    const finalCode = code || twoFACode.join('');
    if (finalCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    setError('');
    const result = await verify2FALogin(finalCode);
    if (!result.success) {
      setError(result.error || 'Invalid verification code');
      setTwoFACode(['', '', '', '', '', '']);
      twoFAInputRefs.current[0]?.focus();
    }
  };

  const handleStart2FASetup = async () => {
    setSetupLoading(true);
    setSetupError('');
    try {
      const data = await enable2FA();
      setSetupQRUrl(data.qr_code_url);
      setSetupSecret(data.secret);
      setSetupBackupCodes(data.backup_codes);
    } catch (err: any) {
      setSetupError(err.message || 'Failed to initiate 2FA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerify2FASetup = async () => {
    if (setupCode.length !== 6) return;
    setSetupLoading(true);
    setSetupError('');
    try {
      await verify2FASetup(setupCode);
      setSetupSuccess(true);
      setTimeout(() => {
        setShow2FASetup(false);
        closeAuthModal();
      }, 2000);
    } catch (err: any) {
      setSetupError(err.message || 'Invalid code. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (authModalMode === 'login') {
      const result = await login(email, password);
      if (result.requires2FA) {
        // 2FA required — store will set requires2FA, UI will show code input
        setError('');
        setTimeout(() => twoFAInputRefs.current[0]?.focus(), 100);
        return;
      }
      if (!result.success) {
        setError(formatErrorMessage(result.error || 'Invalid email or password.'));
      }
    } else {
      if (!firstName.trim()) {
        setError('Please enter your first name');
        return;
      }
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const result = await signup(fullName, email, password);
      if (!result.success) {
        setError(formatErrorMessage(result.error || 'Registration failed. Please try again.'));
      } else {
        // After successful signup, offer optional 2FA setup
        setSignupEmail(email);
        if (wantEnable2FA) {
          setShow2FASetup(true);
        } else {
          setShowCheckEmail(true);
        }
      }
    }
  };

  const switchMode = () => {
    setError('');
    setAuthModalMode(authModalMode === 'login' ? 'signup' : 'login');
  };

  const handleCloseCheckEmail = () => {
    setShowCheckEmail(false);
    closeAuthModal();
  };

  const handleResendFromModal = async () => {
    try {
      await fetch(`${API_URL}/console/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail }),
      });
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    }
  };

  // Show check email modal after signup
  if (showCheckEmail) {
    return (
      <CheckEmailModal
        email={signupEmail}
        onClose={handleCloseCheckEmail}
        onResend={handleResendFromModal}
      />
    );
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      await requestPasswordReset(forgotEmail.trim());
      setForgotSent(true);
    } catch (err: any) {
      // Backend always returns success to prevent email enumeration,
      // but handle network errors gracefully
      if (err?.message?.includes('fetch') || err?.message?.includes('network')) {
        setForgotError('Unable to connect. Please check your internet and try again.');
      } else {
        // Even on unknown errors, show success to prevent email enumeration
        setForgotSent(true);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  if (!showAuthModal) return null;

  // Post-signup 2FA setup screen
  if (show2FASetup) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed inset-0 bg-white dark:bg-gray-900 z-[250] flex flex-col"
        >
          <div className="absolute top-4 right-4 safe-area-top z-10">
            <button
              onClick={() => { setShow2FASetup(false); closeAuthModal(); }}
              data-testid="close-auth-modal"
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col justify-center safe-area-top">
            <div className="w-full max-w-sm mx-auto px-6">
              <div className="mb-6 text-center">
                <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield size={28} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  {setupSuccess ? '2FA Enabled!' : 'Secure your account'}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {setupSuccess
                    ? 'Two-factor authentication is now active.'
                    : 'Add two-factor authentication for extra security.'}
                </p>
              </div>

              {setupSuccess ? (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-center">
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">
                    Your account is now protected with 2FA. Redirecting...
                  </p>
                </div>
              ) : !setupQRUrl ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Smartphone size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">How it works</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Use an authenticator app like Google Authenticator or Authy to generate a verification code each time you sign in.
                        </p>
                      </div>
                    </div>
                  </div>
                  {setupError && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                      {setupError}
                    </div>
                  )}
                  <button
                    onClick={handleStart2FASetup}
                    disabled={setupLoading}
                    className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {setupLoading ? <><Loader2 size={16} className="animate-spin" /> Setting up...</> : 'Set up 2FA'}
                  </button>
                  <button
                    onClick={() => { setShow2FASetup(false); closeAuthModal(); }}
                    className="w-full px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium transition-colors"
                  >
                    Skip for now
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 text-center">Scan this QR code with your authenticator app:</p>
                    <div className="flex justify-center mb-3">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupQRUrl)}`}
                        alt="2FA QR Code"
                        className="w-44 h-44 rounded-lg"
                      />
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      Or enter manually: <code className="text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-[10px]">{setupSecret}</code>
                    </p>
                  </div>
                  {setupBackupCodes.length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">Save your backup codes (shown only once):</p>
                      <div className="grid grid-cols-2 gap-1">
                        {setupBackupCodes.map((code, i) => (
                          <span key={i} className="font-mono text-xs text-gray-600 dark:text-gray-300">{code}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {setupError && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                      {setupError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Enter verification code</label>
                    <input
                      type="text"
                      value={setupCode}
                      onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white text-center tracking-[0.3em] font-mono placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <button
                    onClick={handleVerify2FASetup}
                    disabled={setupLoading || setupCode.length !== 6}
                    className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {setupLoading ? <><Loader2 size={16} className="animate-spin" /> Verifying...</> : 'Verify & Enable'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // 2FA verification during login
  if (requires2FA) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed inset-0 bg-white dark:bg-gray-900 z-[250] flex flex-col"
        >
          <div className="absolute top-4 left-4 safe-area-top z-10">
            <button
              onClick={() => { reset2FAState(); setTwoFACode(['', '', '', '', '', '']); setError(''); }}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft size={22} />
            </button>
          </div>
          <div className="absolute top-4 right-4 safe-area-top z-10">
            <button
              onClick={closeAuthModal}
              data-testid="close-auth-modal"
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col justify-center safe-area-top">
            <div className="w-full max-w-sm mx-auto px-6">
              <div className="mb-8 text-center">
                <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield size={28} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Two-factor authentication
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
                  {error}
                </div>
              )}

              <div className="flex justify-center gap-2 mb-6">
                {twoFACode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { twoFAInputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handle2FACodeChange(index, e.target.value)}
                    onKeyDown={(e) => handle2FAKeyDown(index, e)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                      if (pasted.length === 6) {
                        const digits = pasted.split('');
                        setTwoFACode(digits);
                        handle2FASubmit(pasted);
                      }
                    }}
                    className="w-11 h-14 text-center text-xl font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none transition-colors"
                  />
                ))}
              </div>

              <button
                onClick={() => handle2FASubmit()}
                disabled={isLoading || twoFACode.some(d => d === '')}
                className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? <><Loader2 size={16} className="animate-spin" /> Verifying...</> : 'Verify'}
              </button>

              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-4">
                You can also use a backup code
              </p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Forgot password mode
  if (showForgotPassword) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed inset-0 bg-white dark:bg-gray-900 z-[250] flex flex-col"
        >
          <div className="absolute top-4 left-4 safe-area-top z-10">
            <button
              onClick={() => setShowForgotPassword(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft size={22} />
            </button>
          </div>
          <div className="absolute top-4 right-4 safe-area-top z-10">
            <button
              onClick={() => { setShowForgotPassword(false); closeAuthModal(); }}
              data-testid="close-auth-modal"
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col justify-center safe-area-top">
            <div className="w-full max-w-sm mx-auto px-6">
              <div className="mb-8 text-center">
                <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={28} className="text-primary-600 dark:text-primary-400" />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  {forgotSent ? 'Check your email' : 'Reset password'}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {forgotSent
                    ? `If an account exists for ${forgotEmail}, we've sent a password reset link.`
                    : "Enter your email and we'll send you a link to reset your password."}
                </p>
              </div>

              {forgotSent ? (
                <div className="space-y-4">
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 text-center">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      Check your inbox and spam folder. The link expires in 1 hour.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {forgotError && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                      {forgotError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoFocus
                      className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  const oauthBaseUrl = API_URL;
  const redirectUrl = window.location.origin;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[250] flex flex-col"
      >
        {/* Close button - top right */}
        <div className="absolute top-4 right-4 safe-area-top z-10">
          <button
            onClick={closeAuthModal}
            data-testid="close-auth-modal"
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col justify-center safe-area-top">
          <div className="w-full max-w-sm mx-auto px-6">
            {/* Centered logo + heading */}
            <div className="mb-8 text-center">
              <div className="flex items-center justify-center gap-2.5 mb-4">
                <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="logoGradAuth" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <ellipse cx="20" cy="20" rx="8" ry="17" stroke="url(#logoGradAuth)" strokeWidth="1.5" fill="none" opacity="0.6" />
                  <ellipse cx="20" cy="20" rx="17" ry="8" stroke="url(#logoGradAuth)" strokeWidth="1.5" fill="none" opacity="0.6" />
                  <line x1="3" y1="20" x2="37" y2="20" stroke="#06b6d4" strokeWidth="2" opacity="0.35" />
                  <line x1="20" y1="3" x2="20" y2="37" stroke="#06b6d4" strokeWidth="2" opacity="0.35" />
                  <circle cx="26" cy="14" r="5" fill="url(#logoGradAuth)" />
                  <circle cx="26" cy="14" r="2" fill="white" />
                </svg>
                <span className="text-xl font-semibold text-gray-900 dark:text-white">Coordal</span>
              </div>
              <h1
                data-testid="auth-login-heading"
                className="text-2xl font-semibold text-gray-900 dark:text-white mb-2"
              >
                {authModalMode === 'login' ? 'Welcome back' : 'Create your account'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {authModalMode === 'login'
                  ? 'Sign in to access your saved places and directions'
                  : 'Join Coordal to save places, get directions, and more'}
              </p>
            </div>

            {/* OAuth Buttons */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => {
                  window.location.href = `${oauthBaseUrl}/auth/github/login?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                Continue with GitHub
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = `${oauthBaseUrl}/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500">or</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 dark:border-red-500/50 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {authModalMode === 'signup' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First"
                      className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last"
                      className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Password
                  </label>
                  {authModalMode === 'login' && (
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setForgotSent(false); setForgotError(''); }}
                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none transition-colors pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {authModalMode === 'signup' && (
                <label className="flex items-center gap-2.5 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantEnable2FA}
                    onChange={(e) => setWantEnable2FA(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500"
                  />
                  <div className="flex items-center gap-2">
                    <Shield size={14} className="text-primary-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-300">Enable two-factor authentication</span>
                  </div>
                </label>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {authModalMode === 'login' ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  authModalMode === 'login' ? 'Sign in' : 'Create Account'
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {authModalMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={switchMode}
                className="text-gray-900 dark:text-white font-medium hover:underline"
              >
                {authModalMode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>

        <AuthLegalFooter />
      </motion.div>
    </AnimatePresence>
  );
}
